<template>
	<v-row align="center" justify="center">
		<v-col v-if="!sent">
			<v-form type="POST" @submit.prevent="send" ref="form">
				<v-card width="400px" class="mx-auto px-7 py-7">
					<v-card-title>Forgot password</v-card-title>

					<v-card-text class="mx-1">
						Enter the email address associated with your account and we will send you a link to reset your
						password.
					</v-card-text>

					<v-text-field
						label="Email adress"
						v-model="email"
						:rules="rules"
						:loading="isLoading"
						required
						outlined
						hide-details="auto"
						class="mx-4 pt-5 pb-2"
					/>

					<div class="text-center mx-4 pt-2">
						<VueRecaptcha
							ref="recaptcha"
							@verify="onVerify"
							@expired="onExpired"
							:sitekey="sitekey"
							:loadRecaptchaScript="true"
							style="display: inline-block"
						/>
					</div>

					<v-card-actions class="mx-2">
						<v-btn block color="primary" type="submit">Send</v-btn>
					</v-card-actions>
					<v-card-actions class="mx-2 py-0">
						<v-btn @click="redirect" color="primary" block text>Go back to login</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-col>

		<v-col v-else>
			<v-card width="400px" class="mx-auto px-7 py-7">
				<v-card-text class="text-center">
					Thank you, check your email for instructions on resetting your password.
				</v-card-text>
				<v-card-actions class="mx-2">
					<v-btn @click="redirect" block color="primary">Go back to login</v-btn>
				</v-card-actions>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
	import UserAPI from '@/api/user.js';
	import VueRecaptcha from 'vue-recaptcha';

	/**
	 * @module ForgotPasswordView
	 *
	 * @vue-data {boolean} [sent=false] - Form has been submitted
	 * @vue-data {string} [email=''] - The user email
	 * @vue-data {array<boolean>} [rules=false] - Rules for email validation
	 * @vue-data {boolean} [isLoading=false] - Display progress on button
	 * @vue-data {string} [sitekey=process.env.VUE_APP_GOOGLE_SITE_KEY] - Google sitekey
	 * @vue-data {string} [responseToken=''] - Response token to verify reCAPTCHA v2
	 */

	export default {
		components: { VueRecaptcha },
		data() {
			return {
				sent: false,
				email: '',
				rules: [],
				isLoading: false,
				sitekey: process.env.VUE_APP_GOOGLE_SITE_KEY,
				responseToken: '',
			};
		},

		/**
		 * Listen for value changes
		 *
		 * @property {function} email - Reset email validation
		 */
		watch: {
			email: function() {
				this.rules = [];
			},
		},

		methods: {
            /*
                POST /api/v1/forgot-password
            */

			/**
			 * @async
			 *
			 * @returns {void}
			 *
			 * @description
			 * - Set email validation rules
			 * - Validate form
			 * <br>&nbsp;
			 * - @HTTP 200 & @HTTP 500
			 * - Display progress on button 
             * - Display message that form has been received
			 */
			async send() {
				this.rules = [
					v => !!v || 'The email adress is required.',
					v => /.+@.+\..+/.test(v) || 'The email adress must be valid.',
				];

				if (this.$refs.form.validate()) {
					if (!this.email.trim()) return;

					try {
						this.isLoading = true;

						const { status } = await UserAPI.forgotPassword(this.email);

						if (status === 200 && this.responseToken) {
							this.sent = true;
						}
					} catch ({ response: { status } }) {
						if (status === 500 && this.responseToken) {
							this.sent = true;
						}
					} finally {
						this.isLoading = false;
					}
				}
			},

            /**
             * Redirect user to Login page 
             * 
             * @returns {void}
             */
			redirect() {
				this.$router.push({ name: 'Login' });
			},

            /**
             * Set response token 
             * 
             * @param {string} response - Response token 
             * 
             * @returns {void}
             */
			onVerify(response) {
				this.responseToken = response;
			},

            /**
             * Logs that token is expired 
             * 
             * @returns {void}
             */
			onExpired() {
				console.log('Expired');
			},
		},
	};
</script>

<style scoped>
	.card-title {
		word-break: normal;
	}
</style>
